import type { SaveSocket } from '../../../yage/server/saves/client.mjs';
import { Persist } from "yage/persist/persist";
import { DrawSystemImpl, System } from "minecs";
import type { GameModel, ReadOnlyGameModel } from "yage/game/GameModel";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { AirportState, AirportAgent } from "../components/index.js";
import { ProfileStore, createProfile, type StorageDriver } from "../puzzles/profile.js";
import { LOCAL } from "./local.js";
import { CareerStore } from '../persistence/career-store.js';
import { SaveSchedule } from '../persistence/save-schedule.js';
import { checksumJSON } from '../domain/save-codec.js';
import { createCareerSave } from '../domain/save.js';
import { captureTerminal } from './terminal.js';
import { retainedAirports } from './retained.js';
import { readDiary } from './diary.js';
import { state, career, ids, stations } from './world.js';
import { RemoteCareer, type FamilySaveConfig } from '../persistence/remote-career.js';
import type { SaveData } from '../domain/save.js';
export let CAREERS: CareerStore;
export let initialCareer: {save:SaveData|null;elapsedMs:number;careerId?:string} = {save:null,elapsedMs:0};
let careerWritesEnabled = true;
export let familySave: RemoteCareer | null = null;
const FAMILY_KEY = 'sky-harbor:family-save';
/** UUID v4 that also works on plain-http LAN pages, where crypto.randomUUID is unavailable (insecure context). */
export function randomId(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16)); b[6] = b[6] & 0x0f | 0x40; b[8] = b[8] & 0x3f | 0x80;
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
const validFamily = (c: any): c is FamilySaveConfig => !!c && typeof c.url === 'string' && typeof c.familyId === 'string' && typeof c.credential === 'string'
    && typeof c.careerId === 'string' && /^[a-zA-Z0-9:_-]{1,128}$/.test(c.careerId) && Number.isSafeInteger(c.serverSeq) && (c.personId === undefined || typeof c.personId === 'string');
/**
 * A device joins the family career once, through a `?family=` link (base64url JSON: url, familyId, credential,
 * careerId, optional personId). The connection is remembered on the device and the link is removed from the address bar.
 */
export function rememberedFamily(params: URLSearchParams): FamilySaveConfig | null {
    const link = params.get('family');
    if (link) {
        try {
            const config = { serverSeq: 0, ...JSON.parse(atob(link.replace(/-/g, '+').replace(/_/g, '/'))) };
            if (validFamily(config)) {try {localStorage.setItem(FAMILY_KEY, JSON.stringify(config));} catch {/* remembered for this visit only */}
                const url = new URL(location.href);url.searchParams.delete('family');history.replaceState(null, '', url);return config;}
        } catch {/* fall through to the remembered connection */}
        LOCAL.saveStatus = 'That family link is not valid.';
    }
    for (const store of [() => localStorage, () => sessionStorage]) {
        try {const config = JSON.parse(store().getItem(FAMILY_KEY) ?? 'null');if (validFamily(config)) return config;} catch {/* storage unavailable */}
    }
    return null;
}
function rememberFamily(config: FamilySaveConfig) {try {localStorage.setItem(FAMILY_KEY, JSON.stringify(config));} catch {/* best effort */}}
/** Use the family career for this boot (main.ts decides rooms with it before loading). */
export function useFamily(config: FamilySaveConfig, socket?: SaveSocket) {familySave?.close();familySave = new RemoteCareer(config, socket);return familySave;}
export async function connectFamily(config: FamilySaveConfig, socket?: SaveSocket) {
    const remote = new RemoteCareer(config,socket), result = await remote.load();
    if (!result.ok) {remote.close();return result;}
    const imported = await CAREERS.save(result.save);
    if (!imported.ok) {remote.close();return imported;}
    familySave?.close();familySave = remote;
    rememberFamily(config);
    return imported;
}
export async function loadCareer(fresh = false, checkpoint?: SaveData) {
    careerWritesEnabled = !fresh && !checkpoint;
    if (!fresh && !checkpoint && !familySave) {
        const config = rememberedFamily(new URLSearchParams(typeof location === 'undefined' ? '' : location.search));
        if (config) familySave = new RemoteCareer(config);
    }
    if (checkpoint) {initialCareer = {save:checkpoint,elapsedMs:0};return;}
    let result = fresh ? {save:null,error:null} : await CAREERS.load();
    // The family server holds the shared game state. Take it when it is newer than this device's copy (or a different
    // career); keep the local copy when it already builds on the server's latest, since it may hold unsynced play.
    if (!fresh && familySave) {
        const remote = await familySave.load(), local = result.save;
        if (remote.ok && (!local || local.careerId !== remote.save.careerId || (local.baseSeq ?? 0) < familySave.config.serverSeq)) {
            const cached = await CAREERS.save(remote.save);
            result = cached.ok ? {save:cached.save,error:null} : {save:null,error:cached.error};
        } else if (!remote.ok && remote.missing && local && local.careerId !== familySave.config.careerId) {
            // First device in the family: its career becomes the family career on the next save.
            const adopted = {...local, careerId:familySave.config.careerId, baseSeq:0};adopted.checksum = checksumJSON(adopted)!;
            const cached = await CAREERS.save(adopted);
            if (cached.ok) result = {save:cached.save,error:null};
        } else if (!remote.ok && !remote.missing) LOCAL.saveStatus = remote.error;
    }
    initialCareer = {save:result.save,elapsedMs:result.save ? Math.max(0,Date.now()-result.save.savedAt) : 0,careerId:result.save?.careerId??familySave?.config.careerId??'career:'+randomId()};
    if (result.error) LOCAL.saveStatus = result.error;
}
/** Whether this device has a career to continue (the start menu shows Continue only then). */
export async function savedCareerExists(): Promise<boolean> {
    try {
        // The menu runs before the asset loader opens YAGE storage, so open it here first.
        const persist = Persist.getInstance();
        if (!persist.indexedDB) await persist.init();
        return !!(await CAREERS.load()).save;
    } catch { return false; }
}
/** New game from the start menu: an empty career that autosaves over the old one (unlike ?fresh, which never saves). */
export function startNewCareer() {
    initialCareer = {save:null,elapsedMs:0,careerId:'career:'+randomId()};
}
export async function saveCareer(g: GameModel | ReadOnlyGameModel) {
    const first = g.players[0];
    if (!careerWritesEnabled || first===undefined || !g.localNetIds.includes(g.getTypedUnsafe(PlayerInput,first).pid)) return {ok:false as const,error:'This tab does not own career saves.'};
    const e = state(g);
    const captains = new Map<string,number>((JSON.parse(career(g).savedCaptains) as {personId:string;radioCooldown:number}[]).map(c=>[c.personId,Math.max(0,c.radioCooldown-g.frame)]));
    for (const id of ids(g,AirportAgent)) {const a=g.getTypedUnsafe(AirportAgent,id);captains.set(a.personId,Math.max(0,a.radioCooldown-g.frame));}
    const terminal = captureTerminal(g);
    if (!terminal.ok) {LOCAL.saveStatus = 'Career not saved: '+terminal.error;return {ok:false as const,error:terminal.error};}
    const snapshot = createCareerSave({money:career(g).money,prestige:career(g).prestige,offlineAward:career(g).offlineAward,offlineClaimed:career(g).offlineClaimed,...(career(g).advance>0?{advance:career(g).advance}:{})},terminal.record,Date.now(),{
        careerId:career(g).careerId,baseSeq:familySave?.config.serverSeq??CAREERS.latest?.baseSeq??0,gameTime:career(g).saveGameTime+g.frame-e.loadFrame,
        captains:[...captains].map(([personId,radioCooldown])=>({personId,radioCooldown})),
        diary:readDiary(career(g).diary),
    },retainedAirports(g));
    const result = await CAREERS.save(snapshot);
    LOCAL.saveStatus = result.ok ? 'Career saved on this device' : result.error;
    if (result.ok && familySave) {
        const remote = await familySave.save(result.save);
        LOCAL.saveStatus = remote.ok ? 'Career saved on this device and family server' : remote.error;
        if (remote.ok) rememberFamily(familySave.config);
    }
    return result;
}
export class YageStorage implements StorageDriver {
    readonly persistent = true;
    async get(key: string): Promise<unknown> { const p = Persist.getInstance(); return await p.hasKey(key) ? p.get(key) : undefined; }
    async set(key: string, value: unknown): Promise<void> { await Persist.getInstance().set(key, value); }
    async remove(key: string): Promise<void> { await Persist.getInstance().remove(key); }
}
CAREERS = new CareerStore(new YageStorage());
export let deviceIdentity = 'session';
export function personIdentity(profileId: string): string {return 'device:'+deviceIdentity+':'+checksumJSON({profileId});}
export async function loadProfiles() {
    const store = new ProfileStore(new YageStorage());
    try {
        await store.load();
        const storage = new YageStorage(), id = await storage.get('sky-harbor:device:v1');
        deviceIdentity = typeof id==='string' && /^[a-z0-9-]{1,40}$/.test(id) ? id : randomId();
        if (id!==deviceIdentity) await storage.set('sky-harbor:device:v1',deviceIdentity);
    }
    catch {
        deviceIdentity = randomId();
        store.profiles = [createProfile('captain-1', 'Captain', Date.now())];
        store.activeId = 'captain-1';
        LOCAL.saveStatus = 'Storage unavailable · this session may not be saved';
    }
    LOCAL.profiles = store;
    try {
        const settings = await new YageStorage().get('sky-harbor:settings:v2') as any;
        if (settings) {
            LOCAL.muted = !!settings.muted;
            LOCAL.reducedMotion = !!settings.reducedMotion;
            LOCAL.relaxed = !!settings.relaxed;
            LOCAL.controls = ['tap', 'stick'].includes(settings.controls) ? settings.controls : 'hybrid';
            LOCAL.guidance = settings.guidance === 'experienced' ? 'experienced' : 'full';
            LOCAL.focusedProgression = settings.focusedProgression !== false;
        }
    }
    catch { /* Keep local defaults when device storage is unavailable. */ }
}
export async function saveSettings() { try {
    await new YageStorage().set('sky-harbor:settings:v2', { muted: LOCAL.muted, reducedMotion: LOCAL.reducedMotion, focusedProgression: LOCAL.focusedProgression, relaxed: LOCAL.relaxed, controls: LOCAL.controls, guidance: LOCAL.guidance });
}
catch {
    LOCAL.saveStatus = 'Settings could not be saved.';
} }
@System(AirportState)
export class AirportPersistence extends DrawSystemImpl<ReadOnlyGameModel> {
    static depth = 18000;
    private schedule = new SaveSchedule();
    private model: ReadOnlyGameModel | null = null;
    private hidden = () => { if (document.visibilityState==='hidden' && this.model) void saveCareer(this.model); };
    private exit = () => { if (this.model) void saveCareer(this.model); };
    run = (g: ReadOnlyGameModel) => {
        if (!this.model) {document.addEventListener('visibilitychange',this.hidden);window.addEventListener('pagehide',this.exit);}
        this.model = g;LOCAL.model = g;
        const e = state(g);
        const signature = [e.run,e.mission,e.upgrades,e.hires,e.routesOpened,e.pilotCount,e.arrivalLevel,e.boots,e.bagLevel,e.trashLevel,e.chairLevel,...stations(g).flatMap(s=>[s.unlocked,s.manager,s.level,s.lanes,s.speedLevel,s.incomeLevel,s.dividerLevel])].join(':');
        if (e.loaded && this.schedule.due(g.frame,signature,career(g).money)) void saveCareer(g);
    };
    cleanup = () => {document.removeEventListener('visibilitychange',this.hidden);window.removeEventListener('pagehide',this.exit);this.model = null;};
}
